import { useState, useRef } from 'react'
import { X, Upload, FileText } from 'lucide-react'
import type { LibraryDocument, LibraryTaxonomies } from '@/services/libraryService'

const DOCUMENT_TYPES = [
  { value: 'artigo', label: 'Artigo Científico' },
  { value: 'manual', label: 'Manual Técnico' },
  { value: 'boletim', label: 'Boletim' },
  { value: 'relatorio', label: 'Relatório' },
  { value: 'tese', label: 'Tese/Dissertação' },
  { value: 'guia', label: 'Guia Prático' },
]

interface LibraryDocFormModalProps {
  title: string
  document?: LibraryDocument
  taxonomies: LibraryTaxonomies
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  mode: 'create' | 'edit'
}

export function LibraryDocFormModal({
  title,
  document,
  taxonomies,
  onClose,
  onSubmit,
  mode,
}: LibraryDocFormModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    title: document?.title || '',
    summary: document?.summary || '',
    document_type: document?.document_type || 'artigo',
    author_name: document?.author_name || '',
    source: document?.source || '',
    publication_year: document?.publication_year?.toString() || '',
    status: document?.status || 'draft',
    featured: document?.featured || false,
    taxonomy_ids: document?.taxonomies?.map(t => t.id) || [] as string[],
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      setError('Apenas arquivos PDF são aceitos')
      e.target.value = ''
      return
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo 50MB.')
      e.target.value = ''
      return
    }

    setError('')
    setSelectedFile(file)
  }

  const toggleTaxonomy = (id: string) => {
    setFormData(prev => ({
      ...prev,
      taxonomy_ids: prev.taxonomy_ids.includes(id)
        ? prev.taxonomy_ids.filter(t => t !== id)
        : [...prev.taxonomy_ids, id],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (mode === 'create' && !selectedFile) {
      setError('Selecione um arquivo PDF')
      return
    }

    if (!formData.title.trim()) {
      setError('Título é obrigatório')
      return
    }

    setLoading(true)

    try {
      if (mode === 'create') {
        const fd = new FormData()
        fd.append('title', formData.title)
        if (formData.summary) fd.append('summary', formData.summary)
        fd.append('document_type', formData.document_type)
        if (formData.author_name) fd.append('author_name', formData.author_name)
        if (formData.source) fd.append('source', formData.source)
        if (formData.publication_year) fd.append('publication_year', formData.publication_year)
        fd.append('status', formData.status)
        fd.append('featured', formData.featured.toString())
        if (formData.taxonomy_ids.length > 0) {
          fd.append('taxonomy_ids', JSON.stringify(formData.taxonomy_ids))
        }
        fd.append('file', selectedFile!)
        await onSubmit(fd)
      } else {
        await onSubmit({
          title: formData.title,
          summary: formData.summary || undefined,
          document_type: formData.document_type,
          author_name: formData.author_name || undefined,
          source: formData.source || undefined,
          publication_year: formData.publication_year ? parseInt(formData.publication_year) : undefined,
          status: formData.status,
          featured: formData.featured,
          taxonomy_ids: formData.taxonomy_ids,
        })
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar documento')
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex justify-between items-center shrink-0">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>
          )}

          {/* PDF Upload (only on create) */}
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arquivo PDF *
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="h-8 w-8 text-orange-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Clique para selecionar o PDF</p>
                    <p className="text-xs text-gray-400 mt-1">Máximo 50MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              placeholder="Título do documento"
            />
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resumo</label>
            <textarea
              value={formData.summary}
              onChange={e => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              placeholder="Breve resumo do documento..."
            />
          </div>

          {/* Row: Type + Year */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento</label>
              <select
                value={formData.document_type}
                onChange={e => setFormData({ ...formData, document_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              >
                {DOCUMENT_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ano de Publicação</label>
              <input
                type="number"
                value={formData.publication_year}
                onChange={e => setFormData({ ...formData, publication_year: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                placeholder="2024"
                min="1900"
                max="2100"
              />
            </div>
          </div>

          {/* Row: Author + Source */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Autor(es)</label>
              <input
                type="text"
                value={formData.author_name}
                onChange={e => setFormData({ ...formData, author_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                placeholder="Nome dos autores"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fonte/Instituição</label>
              <input
                type="text"
                value={formData.source}
                onChange={e => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                placeholder="Embrapa, ESALQ/USP..."
              />
            </div>
          </div>

          {/* Categories */}
          {taxonomies.categories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categorias</label>
              <div className="flex flex-wrap gap-2">
                {taxonomies.categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleTaxonomy(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${formData.taxonomy_ids.includes(cat.id)
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {taxonomies.tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tags (Cultura / Bioma)</label>
              <div className="flex flex-wrap gap-2">
                {taxonomies.tags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTaxonomy(tag.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${formData.taxonomy_ids.includes(tag.id)
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Row: Status + Featured */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="draft">Rascunho</option>
                <option value="published">Publicado</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={e => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Destaque na biblioteca</span>
              </label>
            </div>
          </div>

          {/* Loading indicator for PDF processing */}
          {loading && mode === 'create' && (
            <div className="bg-blue-50 p-4 rounded-md">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Enviando PDF...</p>
                  <p className="text-xs text-blue-600 mt-0.5">Aguarde enquanto o arquivo é processado.</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-500 disabled:opacity-50 font-medium text-sm"
            >
              {loading ? 'Salvando...' : mode === 'create' ? 'Upload & Criar' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
